import * as React from 'react';
import { deepCompare } from '@agilepoint/utils-common';
import styled from 'styled-components';
import { IColorPickerProps } from './color-picker-props';
import { SaturationPicker, HuePicker, OpacityPicker, ColorInput } from './parts';
import { IHSV, IRGB } from './parts/types';
import { getColorFromString, getColorFromRGBA, IColor, updateH, updateSV, updateA, getFullColorString, getOpaqueColorString } from '../../utils';

const ColorPickerSurface = styled.div.attrs((props: Pick<IColorPickerProps, 'pickerSurfaceWidth'>) => props)`
    display: inline-block;
    width: ${props => props.pickerSurfaceWidth}px;
`;

const StyledSaturationPicker = styled(SaturationPicker)`
    margin-top: 12px;
`;

const StyledOpacityPicker = styled(OpacityPicker)`
    margin-top: 12px;
`;

interface IColorPickerState {
    oldValue?: string;
    color: IColor;
}

export class ColorPicker extends React.Component<IColorPickerProps, IColorPickerState> {
    static defaultProps = {
        defaultValue: '#ff0000',
        opacityEnabled: true,
        pickerSurfaceHeight: 357,
        pickerSurfaceWidth: 357
    };

    constructor(props: IColorPickerProps) {
        super(props);
        this.state = { oldValue: props.value, color: getColorFromString(props.value! || props.defaultValue!)! };
        if (!this.props.componentRef) {
            return;
        }
        this.props.componentRef.current = {
            getColor: () => this.state.color.str,
            setColor: this.parseAndSetColor
        };
    }

    componentDidUpdate(prevProps: IColorPickerProps, prevState: IColorPickerState) {
        if (this.props.value === undefined ||
            prevProps.value === this.props.value ||
            this.props.value === prevState.oldValue) {
            return;
        }
        const nextColor = getColorFromString(this.props.value!);
        if (!nextColor) {
            return;
        }
        return this.setState({ oldValue: this.props.value, color: nextColor });
    }

    render() {

        const { className, opacityEnabled, pickerSurfaceHeight, pickerSurfaceWidth } = this.props;
        const { color } = this.state;

        const hueColor = getFullColorString(color);
        const opaqueColor = getOpaqueColorString(color);

        const activeDimensionRatio = (opacityEnabled ? 0.85 : 1);
        const saturationPickerWidth = pickerSurfaceWidth! * activeDimensionRatio;
        const saturationPickerHeight = pickerSurfaceHeight! * activeDimensionRatio;

        return (
            <ColorPickerSurface className={className} pickerSurfaceWidth={pickerSurfaceWidth}>
                <ColorInput opacityEnabled={opacityEnabled} color={color} onValueChange={this.onRGBAChanged} />
                <HuePicker value={color.h} dimensionSize={pickerSurfaceWidth} onValueChange={this.onHueChanged} />
                <StyledSaturationPicker color={color} hueColor={hueColor}
                    height={saturationPickerHeight} width={saturationPickerWidth} onValueChange={this.onSaturationChanged} />
                {opacityEnabled ? (
                    <StyledOpacityPicker value={color.a === undefined ? 100 : color.a} baseColor={opaqueColor}
                        dimensionSize={saturationPickerHeight}
                        onValueChange={this.onOpacityChanged} />
                ) : null}
            </ColorPickerSurface>
        );
    }

    private parseAndSetColor = (colorValue: string) => {
        const { value } = this.props;
        // If the component in controlled mode, then we should allow users to set the value through imperative api
        if (value !== undefined) {
            return;
        }
        const newColor = getColorFromString(colorValue);
        if (newColor === undefined) {
            return;
        }
        this.setState({ color: newColor });

    }

    private updateColor = (newColor: IColor) => {
        if (!newColor) {
            return;
        }
        const { color } = this.state;
        if (deepCompare(newColor, color)) {
            return;
        }
        this.setState({ oldValue: newColor.str, color: newColor }, () => {
            const { onValueChange } = this.props;
            if (onValueChange === undefined) {
                return;
            }
            onValueChange(newColor.str);
        });
    }

    private onHueChanged = (value: number) => {
        const { color } = this.state;
        this.updateColor(updateH(color, value));
    }

    private onSaturationChanged = (value: IHSV) => {
        const { color } = this.state;
        this.updateColor(updateSV(color, value.s, value.v));
    }

    private onOpacityChanged = (value: number) => {
        const { color } = this.state;
        this.updateColor(updateA(color, value));
    }

    private onRGBAChanged = (value: IRGB) => {
        this.updateColor(getColorFromRGBA(value));
    }
}
